import { compareWithLocale } from '@disy/lib/locale';
import { cadenzaLocalStorage } from '@disy/lib/storage';

export interface CompletionSource {
  completionCandidates(): string[];

  onUrlAdded(urlEntered: string): void;
}

export class DelegatingCompletionSource implements CompletionSource {
  _sources: CompletionSource[];

  constructor(...sources: CompletionSource[]) {
    this._sources = sources;
  }

  completionCandidates() {
    const candidates = this._sources.flatMap((s) => s.completionCandidates());
    return [...new Set(candidates)].sort(compareWithLocale);
  }

  onUrlAdded(urlEntered: string) {
    this._sources.forEach((source) => source.onUrlAdded(urlEntered));
  }
}

export class LocalStorageCompletionSource implements CompletionSource {
  _storageKey: string;

  constructor(storageKey: string) {
    this._storageKey = storageKey;
  }

  completionCandidates() {
    return this._values();
  }

  onUrlAdded(urlEntered: string) {
    if (urlEntered) {
      const existingValues = this._values();
      const newValues = addAtLastPositionAndReturnArrayWithMaximumLength(urlEntered, existingValues, 5);
      cadenzaLocalStorage.setItem(this._storageKey, JSON.stringify(newValues));
    }
  }

  _values(): string[] {
    const serialized = cadenzaLocalStorage.getItem(this._storageKey);
    return serialized ? JSON.parse(serialized) : [];
  }
}

function addAtLastPositionAndReturnArrayWithMaximumLength(
  newValue: string,
  existingValues: string[],
  maximumLength: number,
) {
  // remove the newValue from the existingValues and put it at the end afterwards
  const filteredValues = existingValues.filter((existingValue) => existingValue !== newValue);
  filteredValues.push(newValue);
  // deduplicate
  const uniqueValues = Array.from(new Set(filteredValues));
  if (uniqueValues.length > maximumLength) {
    uniqueValues.splice(0, uniqueValues.length - maximumLength);
  }
  return uniqueValues;
}

export class NoopCompletionSource implements CompletionSource {
  completionCandidates() {
    return [];
  }

  onUrlAdded(urlEntered: string) {
    // do nothing
  }
}
